import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUnsavedChangesCtx } from "../contexts/unsavedChanges";

export type Blocker = {
  state: "idle" | "blocked";
  proceed: () => void;
  reset: () => void;
};

export function useUnsavedChanges() {
  const { isDirtyRef, block } = useUnsavedChangesCtx();
  const [isDirty, setIsDirty] = useState(false);
  const savedDataRef = useRef<string>("");
  const navigate = useNavigate();

  const markSaved = useCallback((data: unknown) => {
    savedDataRef.current = JSON.stringify(data);
    isDirtyRef.current = false;
    setIsDirty(false);
  }, [isDirtyRef]);

  const checkDirty = useCallback((currentData: unknown) => {
    const dirty = JSON.stringify(currentData) !== savedDataRef.current;
    isDirtyRef.current = dirty;
    setIsDirty(dirty);
  }, [isDirtyRef]);

  // Intercetta tasto indietro/avanti del browser
  useEffect(() => {
    if (!isDirty) return;

    window.history.pushState(null, "", window.location.href);

    const handlePopState = () => {
      if (!isDirtyRef.current) return;
      window.history.pushState(null, "", window.location.href);
      block(
        () => {
          isDirtyRef.current = false;
          setIsDirty(false);
          window.history.go(-1);
        },
        () => {}
      );
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isDirty, isDirtyRef, block]);

  // beforeunload per chiusura tab/finestra
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirtyRef]);

  // Navigazione programmatica con guardia modale
  const safeNavigate = useCallback(
    (to: string) => {
      console.log(isDirtyRef.current, "isDirtyRef.current");
      if (!isDirtyRef.current) {
        navigate(to);
        return;
      }
      block(
        () => {
          isDirtyRef.current = false;
          setIsDirty(false);
          navigate(to);
        },
        () => {}
      );
    },
    [navigate, isDirtyRef, block]
  );

  return { isDirty, markSaved, checkDirty, safeNavigate };
}
